// Componentes de presentación del design-system propio: Badge (variantes de estado), Loading
// (spinner), Skeleton, EmptyState, ErrorState, CopyableId (id copiable — la excepción bendecida a
// la regla anti-UUID), HelpTooltip (ícono ? con tooltip) y Tabs. Reexporta además la familia Card
// (base shadcn).
import { forwardRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { AlertTriangle, Check, Copy, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { badgeVariants, type BadgeProps as UiBadgeProps } from '@/components/ui/badge';
import {
    Card as UiCard,
    CardContent as UiCardContent,
    CardDescription as UiCardDescription,
    CardHeader as UiCardHeader,
    CardTitle as UiCardTitle,
} from '@/components/ui/card';
import { HelpHint } from '@/components/ui/HelpHint';

// Sub-partes de Card reexportadas para composición manual (base shadcn).
export {
    CardHeader,
    CardFooter,
    CardTitle,
    CardDescription,
    CardContent,
} from '@/components/ui/card';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
    title?: ReactNode;
    description?: ReactNode;
}

// Card del kit: contenedor con header opcional. Si se pasa title/description arma el header;
// si no, se comporta como contenedor simple (admite CardHeader/CardContent como children).
export const Card = forwardRef<HTMLDivElement, CardProps>(
    ({ title, description, children, className, ...props }, ref) => {
        if (!title && !description) {
            return (
                <UiCard ref={ref} className={className} {...props}>
                    {children}
                </UiCard>
            );
        }
        return (
            <UiCard ref={ref} className={className} {...props}>
                <UiCardHeader>
                    {title ? <UiCardTitle>{title}</UiCardTitle> : null}
                    {description ? <UiCardDescription>{description}</UiCardDescription> : null}
                </UiCardHeader>
                <UiCardContent>{children}</UiCardContent>
            </UiCard>
        );
    },
);
Card.displayName = 'Card';

type BadgeVariant = NonNullable<UiBadgeProps['variant']>;
type BadgeSize = NonNullable<UiBadgeProps['size']>;
const KNOWN_BADGE_VARIANTS: string[] = [
    'default',
    'secondary',
    'destructive',
    'success',
    'warning',
    'info',
    'danger',
    'muted',
    'outline',
];

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
    variant?: string;
    /** `xs` = el chip denso del mock, el que cabe en una fila de 28 px. */
    size?: BadgeSize;
}

export function Badge({ variant, size, className, ...props }: BadgeProps) {
    // Mapeo tolerante: 'error' -> 'destructive'; desconocidas -> 'default'. Así cualquier variante
    // que venga por expresión (status.variant, meta.variant) compila y pinta algo razonable.
    const normalized = variant === 'error' ? 'destructive' : (variant ?? 'default');
    const safe = (
        KNOWN_BADGE_VARIANTS.includes(normalized) ? normalized : 'default'
    ) as BadgeVariant;
    return <div className={cn(badgeVariants({ variant: safe, size }), className)} {...props} />;
}

export interface LoadingProps {
    text?: string;
    size?: 'sm' | 'md' | 'lg';
    /** Centra el spinner en toda la ventana (para pending del router / carga inicial). */
    fullScreen?: boolean;
    className?: string;
}

const LOADING_SIZE: Record<string, string> = { sm: 'size-4', md: 'size-6', lg: 'size-8' };

export function Loading({ text, size = 'md', fullScreen = false, className }: LoadingProps) {
    return (
        <div
            className={cn(
                'flex items-center justify-center gap-2 text-muted-foreground',
                fullScreen ? 'min-h-screen w-full' : 'py-6',
                className,
            )}
        >
            <Loader2 className={cn('animate-spin', LOADING_SIZE[size])} aria-hidden />
            {text ? <span className="text-sm">{text}</span> : null}
        </div>
    );
}

export interface EmptyStateProps {
    title: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    action?: ReactNode;
    className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-6 py-12 text-center',
                className,
            )}
        >
            {icon ? <div className="text-muted-foreground">{icon}</div> : null}
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {description ? (
                <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
            ) : null}
            {action ? <div className="mt-2">{action}</div> : null}
        </div>
    );
}

export interface ErrorStateProps {
    /** Qué falló, en una línea. Default: "No se pudieron cargar los datos". */
    title?: ReactNode;
    /** Detalle accionable — típicamente el `message` del error. */
    description?: ReactNode;
    /** Si viene, se pinta el botón "Reintentar" (típico: el `refetch` de TanStack Query). */
    onRetry?: () => void;
    className?: string;
}

/**
 * Gemelo de `EmptyState` para el camino triste. Vacío y error NO son lo mismo: uno dice "no hay
 * nada que mostrar", el otro "no pudimos traerlo" — y solo el segundo invita a reintentar.
 * Confundirlos hace que un backend caído se lea como un padrón vacío.
 */
export function ErrorState({
    title = 'No se pudieron cargar los datos',
    description,
    onRetry,
    className,
}: ErrorStateProps) {
    return (
        <div
            role="alert"
            className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-destructive/40 bg-destructive/5 px-6 py-10 text-center',
                className,
            )}
        >
            <AlertTriangle className="size-5 text-destructive" aria-hidden />
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {description ? (
                <p className="max-w-sm text-[12.5px] text-muted-foreground">{description}</p>
            ) : null}
            {onRetry ? (
                <button
                    type="button"
                    onClick={onRetry}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-[11.5px] font-medium transition-colors hover:bg-accent"
                >
                    <RotateCcw className="size-3" aria-hidden />
                    Reintentar
                </button>
            ) : null}
        </div>
    );
}

export interface SkeletonProps {
    /** Cuántas barras apilar (default 1) — para simular filas de tabla mientras carga. */
    lines?: number;
    className?: string;
}

/**
 * Bloque pulsante para contenido de forma CONOCIDA (filas de tabla, líneas de una ficha). Cuando
 * la forma no se conoce de antemano, `Loading` (spinner) comunica mejor que un esqueleto que
 * miente sobre lo que va a aparecer.
 */
export function Skeleton({ lines = 1, className }: SkeletonProps) {
    if (lines <= 1) {
        return <div aria-hidden className={cn('h-4 animate-pulse rounded bg-muted', className)} />;
    }
    return (
        <div className="flex flex-col gap-2" aria-hidden>
            {Array.from({ length: lines }, (_unused, i) => (
                <div key={i} className={cn('h-4 animate-pulse rounded bg-muted', className)} />
            ))}
        </div>
    );
}

export interface CopyableIdProps {
    label: string;
    value: string;
    className?: string;
    /** Una sola línea «label value» truncada con ícono de copiar al lado, para celdas de tabla (DEC-47). */
    inline?: boolean;
    /** Parte el valor en varias líneas en vez de truncarlo, para folios largos en un modal. */
    wrap?: boolean;
    /** Texto a mostrar si difiere del que se copia; en `inline` el title conserva el valor completo. */
    display?: string;
}

export function CopyableId({
    label,
    value,
    className,
    inline = false,
    wrap = false,
    display,
}: CopyableIdProps) {
    const shown = display ?? value;
    const [copied, setCopied] = useState(false);
    const copy = () => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- el tipo DOM marca clipboard como siempre presente, pero falta en contextos no seguros / jsdom
        if (!navigator.clipboard) return;
        void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            if (inline) toast.success('Copiado', { duration: 1500 });
            setTimeout(() => {
                setCopied(false);
            }, 1500);
        });
    };
    const Icon = copied ? Check : Copy;

    if (inline) {
        const text = `${label} ${value}`;
        return (
            <span className={cn('flex min-w-0 max-w-full items-center gap-1', className)}>
                <span className="truncate font-mono" title={text}>
                    {`${label} ${shown}`}
                </span>
                <button
                    type="button"
                    aria-label={`Copiar ${label}`}
                    title={`Copiar ${label}`}
                    onClick={copy}
                    className="inline-flex shrink-0 items-center rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <Icon className={cn('size-3', copied && 'text-success')} aria-hidden />
                </button>
            </span>
        );
    }

    return (
        <div className={cn('flex min-w-0 max-w-full flex-col gap-1', className)}>
            <span className="text-xs font-medium text-muted-foreground">{label}</span>
            <button
                type="button"
                aria-label={label}
                title={`Copiar ${label}`}
                onClick={copy}
                className="inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-left font-mono text-xs text-foreground transition-colors hover:bg-muted"
            >
                <span className={cn('min-w-0', wrap ? 'break-all' : 'truncate')}>{shown}</span>
                <Icon
                    className={cn('size-3.5 shrink-0', copied ? 'text-success' : 'opacity-60')}
                    aria-hidden
                />
            </button>
        </div>
    );
}

export interface HelpTooltipProps {
    content: string;
    /** Nombre accesible del `?`. Con varios en la misma pantalla, «Ayuda» a secas no distingue. */
    label?: string;
    side?: 'top' | 'right' | 'bottom' | 'left';
    className?: string;
}

export function HelpTooltip({ content, label, side, className }: HelpTooltipProps) {
    return <HelpHint text={content} label={label} side={side} className={className} />;
}

export interface TabItem {
    key: string;
    label: string;
}

export interface TabsProps {
    tabs: readonly TabItem[];
    activeTab: string;
    onChange: (key: string) => void;
    className?: string;
    /**
     * Reparte las pestañas en varias filas en vez de dejarlas scrollear. Para tiras largas en
     * paneles angostos, donde el scroll horizontal ESCONDE pestañas sin decir que existen.
     */
    wrap?: boolean;
}

export function Tabs({ tabs, activeTab, onChange, className, wrap = false }: TabsProps) {
    return (
        // `overflow-x-auto` + `shrink-0` en los botones: sin esto, una tira de pestañas que no entra
        // en su contenedor NO se acomoda — se sale de la tarjeta y empuja el ancho de la página
        // (visto con 5 pestañas en un panel de 21rem: "Bitácora" quedaba fuera del borde). Cuando las
        // pestañas SÍ entran no cambia nada, así que es seguro para el resto de las pantallas.
        <div
            className={cn(
                'flex gap-1 border-b border-border',
                wrap ? 'flex-wrap' : 'overflow-x-auto',
                className,
            )}
            role="tablist"
        >
            {tabs.map((tab) => {
                const active = tab.key === activeTab;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => {
                            onChange(tab.key);
                        }}
                        className={cn(
                            'shrink-0 whitespace-nowrap border-b-2 font-medium transition-colors',
                            wrap ? 'px-2.5 py-1.5 text-[12.5px]' : 'px-4 py-2 text-sm',
                            active
                                ? 'border-primary text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}
